import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft, Copy, ShieldOff, Clock, Scale, Mail, Ticket,
  CheckCircle, AlertTriangle, X, Ban, Timer
} from 'lucide-react';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const statusColors = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  redeemed: 'bg-gray-200 text-gray-700 border-gray-300',
  suspended: 'bg-red-100 text-red-700 border-red-300',
  expired: 'bg-orange-100 text-orange-700 border-orange-300',
  blocked: 'bg-red-200 text-red-800 border-red-400',
};

const timelineIcons = {
  issued: { icon: Ticket, color: 'bg-[#561D1A]' },
  redemption: { icon: CheckCircle, color: 'bg-[#EC1A23]' },
  adjustment: { icon: Scale, color: 'bg-amber-500' },
  expired: { icon: Timer, color: 'bg-gray-500' },
};

function ConfirmDialog({ title, message, onConfirm, onCancel, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{message}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function VoucherDetailView({ voucherId, onBack }) {
  const { currency: activeCurrency, symbol, formatDateTime: formatDate, formatShortDate } = useCurrency();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'block' | 'expire' | 'adjust' | null
  const [reason, setReason] = useState('');
  const [newBalance, setNewBalance] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/voucher/admin/vouchers/${voucherId}`);
      setVoucher(res.data);
    } catch {
      toast.error('Failed to load voucher');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [voucherId]);

  const copyCode = () => {
    navigator.clipboard?.writeText(voucher?.code);
    toast.success('Code copied');
  };

  const handleAction = async (action) => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setActionLoading(true);
    try {
      if (action === 'block') {
        await api.put(`/api/voucher/admin/vouchers/${voucherId}/block`, { reason });
        toast.success('Voucher blocked');
      } else if (action === 'expire') {
        await api.put(`/api/voucher/admin/vouchers/${voucherId}/force-expire`, { reason });
        toast.success('Voucher force-expired');
      } else if (action === 'adjust') {
        const nb = parseFloat(newBalance);
        if (isNaN(nb) || nb < 0) { toast.error('Invalid balance'); setActionLoading(false); return; }
        await api.put(`/api/voucher/admin/vouchers/${voucherId}/adjust-balance`, { reason, new_balance: nb });
        toast.success('Balance adjusted');
      }
      setDialog(null);
      setReason('');
      setNewBalance('');
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const res = await api.post(`/api/voucher/admin/vouchers/${voucherId}/resend`);
      if (res.data.sent) {
        toast.success(`Voucher details sent to ${res.data.email}`);
      } else {
        toast.info('SMTP not configured. Email not sent.');
      }
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to resend');
    }
  };

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-24 bg-gray-100 rounded animate-pulse" />
      <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
  );

  if (!voucher) return (
    <div className="text-center py-16 text-gray-400">
      <p>Voucher not found</p>
      <button onClick={onBack} className="mt-3 text-sm text-[#561D1A] hover:underline">Go back</button>
    </div>
  );

  const displayStatus = voucher.status === 'active' && voucher.balance < voucher.amount ? 'partial' : voucher.status;
  const isActionable = ['active', 'suspended'].includes(voucher.status) || displayStatus === 'partial';

  return (
    <div data-testid="voucher-detail">
      {/* Back + Header */}
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#561D1A] mb-4 transition-colors" data-testid="back-to-list">
        <ArrowLeft size={15} /> Back to Vouchers
      </button>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#561D1A] font-mono tracking-wider" data-testid="voucher-code-header">{voucher.code}</h2>
        <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${statusColors[displayStatus] || 'bg-gray-100 text-gray-600'}`} data-testid="voucher-status-pill">
          {displayStatus === 'partial' ? 'Partially Redeemed' : voucher.status?.charAt(0).toUpperCase() + voucher.status?.slice(1)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Voucher Info */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="voucher-info-card">
            <h3 className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-4">Voucher Information</h3>

            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Code</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-gray-800">{voucher.code}</span>
                  <button onClick={copyCode} className="text-gray-400 hover:text-[#561D1A]" data-testid="copy-code-btn"><Copy size={13} /></button>
                </div>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Initial Value</span>
                <span className="text-sm font-bold text-[#561D1A]">{formatAmount(voucher.amount, activeCurrency)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Current Balance</span>
                <span className={`text-sm font-bold ${voucher.balance < voucher.amount ? 'text-orange-600' : 'text-emerald-600'}`}>
                  {formatAmount(voucher.balance ?? voucher.amount, activeCurrency)}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Currency</span>
                <span className="text-sm text-gray-700">{activeCurrency}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Expiry Date</span>
                <span className="text-sm text-gray-700">{formatShortDate(voucher.expiry_date)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Status</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusColors[displayStatus] || 'bg-gray-100'}`}>
                  {displayStatus === 'partial' ? 'Partially Redeemed' : voucher.status}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Purchase Channel</span>
                <span className="text-sm text-gray-700 capitalize">{voucher.purchase_channel || 'Online'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-500">Type</span>
                <span className="text-sm text-gray-700">{voucher.type_name}</span>
              </div>
            </div>

            {/* Owner Info */}
            {voucher.owner_name && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h4 className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Purchased By</h4>
                <p className="text-sm font-medium text-gray-800">{voucher.owner_name}</p>
                <p className="text-xs text-gray-500">{voucher.owner_email}</p>
                {voucher.owner_phone && <p className="text-xs text-gray-500">{voucher.owner_phone}</p>}
              </div>
            )}

            {voucher.recipient_email && (
              <div className="mt-3 bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-amber-700 font-medium">Gift recipient: {voucher.recipient_email}</p>
                {voucher.gift_message && <p className="text-xs text-amber-600 italic mt-1">"{voucher.gift_message}"</p>}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="voucher-actions">
            <h3 className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-4">Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              {isActionable && (
                <>
                  <button
                    onClick={() => { setDialog('block'); setReason(''); }}
                    className="flex items-center justify-center gap-1.5 bg-red-50 text-red-700 hover:bg-red-100 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors border border-red-200"
                    data-testid="block-voucher-btn"
                  >
                    <Ban size={13} /> Block
                  </button>
                  <button
                    onClick={() => { setDialog('expire'); setReason(''); }}
                    className="flex items-center justify-center gap-1.5 bg-orange-50 text-orange-700 hover:bg-orange-100 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors border border-orange-200"
                    data-testid="force-expire-btn"
                  >
                    <Clock size={13} /> Force Expire
                  </button>
                  <button
                    onClick={() => { setDialog('adjust'); setReason(''); setNewBalance(''); }}
                    className="flex items-center justify-center gap-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors border border-amber-200"
                    data-testid="adjust-balance-btn"
                  >
                    <Scale size={13} /> Adjust Balance
                  </button>
                </>
              )}
              {voucher.owner_id && (
                <button
                  onClick={handleResend}
                  className="flex items-center justify-center gap-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors border border-blue-200"
                  data-testid="resend-voucher-btn"
                >
                  <Mail size={13} /> Resend
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right: Activity Timeline */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="activity-timeline">
            <h3 className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-6">Activity Timeline</h3>
            {voucher.timeline?.length > 0 ? (
              <div className="relative">
                <div className="absolute left-[15px] top-0 bottom-0 w-px bg-gray-200" />
                <div className="space-y-4">
                  {voucher.timeline.map((event, i) => {
                    const config = timelineIcons[event.type] || timelineIcons.issued;
                    const EventIcon = config.icon;
                    return (
                      <div key={i} className="relative flex gap-4 pl-0" data-testid="timeline-event">
                        <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center z-10 flex-shrink-0`}>
                          <EventIcon size={14} className="text-white" />
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-sm font-medium text-gray-800">{event.description}</p>
                            <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDate(event.date)}</span>
                          </div>
                          {event.details && <p className="text-xs text-gray-500 mt-0.5">{event.details}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400 text-sm">No activity recorded</div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      {dialog === 'block' && (
        <ConfirmDialog
          title="Block Voucher"
          message="This action will block the voucher immediately. The voucher will no longer be redeemable. This action cannot be easily undone."
          onConfirm={() => handleAction('block')}
          onCancel={() => setDialog(null)}
        >
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Reason *</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Explain why this voucher should be blocked..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:border-[#561D1A] resize-none"
                data-testid="action-reason-input"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDialog(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleAction('block')} disabled={actionLoading}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50" data-testid="confirm-action-btn">
                {actionLoading ? 'Blocking...' : 'Confirm Block'}
              </button>
            </div>
          </div>
        </ConfirmDialog>
      )}

      {dialog === 'expire' && (
        <ConfirmDialog
          title="Force Expire Voucher"
          message="This will immediately expire the voucher. Any remaining balance will become unusable. This action cannot be undone."
          onConfirm={() => handleAction('expire')}
          onCancel={() => setDialog(null)}
        >
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Reason *</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Explain why this voucher should be force-expired..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:border-[#561D1A] resize-none"
                data-testid="action-reason-input"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDialog(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleAction('expire')} disabled={actionLoading}
                className="flex-1 py-2.5 rounded-xl bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50" data-testid="confirm-action-btn">
                {actionLoading ? 'Expiring...' : 'Confirm Force Expire'}
              </button>
            </div>
          </div>
        </ConfirmDialog>
      )}

      {dialog === 'adjust' && (
        <ConfirmDialog
          title="Adjust Voucher Balance"
          message="You are about to modify the voucher balance. This change will be logged with your reason."
          onConfirm={() => handleAction('adjust')}
          onCancel={() => setDialog(null)}
        >
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600">
              Current balance: <strong className="text-[#561D1A]">{formatAmount(voucher.balance ?? voucher.amount, activeCurrency)}</strong>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">New Balance ({symbol}) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newBalance}
                onChange={e => setNewBalance(e.target.value)}
                placeholder="Enter new balance..."
                className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:border-[#561D1A]"
                data-testid="new-balance-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Reason *</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Mandatory reason for this adjustment..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none focus:border-[#561D1A] resize-none"
                data-testid="action-reason-input"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDialog(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleAction('adjust')} disabled={actionLoading}
                className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50" data-testid="confirm-action-btn">
                {actionLoading ? 'Adjusting...' : 'Confirm Adjustment'}
              </button>
            </div>
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
